/**
 * Legacy toast adapters used by Utils consumers.
 * Routes through operationFeedback toast queue and respects dialog ownership.
 */
import {
  hasMeaningfulErrors,
  SIMPLE_SUCCESS_TOAST_MS,
} from "./types";
import {
  promangToast,
  shouldSuppressToastForOperation,
} from "./toastQueue";

/** Presentation-aware toast — max one visible; defers when dialog owns. */
const toast = {
  success: (message, options) => promangToast.success(message, options),
  error: (message, options) => promangToast.error(message, options),
  loading: (message, options) => promangToast.loading(message, options),
  dismiss: (id) => promangToast.dismiss(id),
  promise: (p, msgs, options) => promangToast.promise(p, msgs, options),
};

/**
 * Build a structured Error from a rejected (or false-success) thunk action.
 * @param {object} action
 * @param {{ emitToast?: boolean }} [opts] When false, skip toast side-effects
 *   (toast.promise / dialog owns a single presentation).
 */
function errorFromRejectedThunk(action, { emitToast = true } = {}) {
  const payload = action?.payload;
  const serializedError = action?.error;
  const errOpts = { persistent: true };

  let message =
    serializedError?.message ||
    (typeof payload === "string" ? payload : null) ||
    payload?.message ||
    (typeof payload?.error === "string" ? payload.error : null) ||
    "Request failed";

  const errs = payload?.errors;
  if (hasMeaningfulErrors(errs)) {
    const flat = flattenErrorMessages(errs);
    if (flat.length) message = flat[0];
    if (emitToast) {
      flat.forEach((msg) => toast.error(msg, errOpts));
    }
  } else if (emitToast) {
    if (typeof payload === "string") {
      toast.error(payload, errOpts);
    } else if (payload?.message) {
      toast.error(payload.message, errOpts);
    } else if (payload?.error) {
      toast.error(
        typeof payload.error === "string"
          ? payload.error
          : String(payload.error),
        errOpts
      );
    } else if (serializedError?.message) {
      toast.error(serializedError.message, errOpts);
    } else if (payload === false) {
      toast.error("Something went wrong. Could not save data!", errOpts);
    } else {
      toast.error("Something went wrong. Could not save data!", errOpts);
    }
  }

  return Object.assign(new Error(message), { payload });
}

/**
 * Soft envelopes: AxiosError / Error / HTTP ≥400 resolved into the success path
 * (legacy catch return) must never look like save success.
 */
function isResolvedFailureEnvelope(res) {
  if (res == null || typeof res !== "object") return false;
  if (res.isAxiosError === true) return true;
  if (typeof Error !== "undefined" && res instanceof Error) return true;
  if (typeof res.status === "number" && res.status >= 400) return true;
  return false;
}

/** Agreements-era thunks fulfilled with `false` — explicit failure, not success. */
function isFalsyThunkPayload(payload) {
  return payload === false;
}

function flattenErrorMessages(errors) {
  if (!hasMeaningfulErrors(errors)) return [];
  if (Array.isArray(errors)) return errors.map((e) => String(e));
  if (typeof errors === "string") return [errors];
  if (typeof errors === "object") {
    return Object.entries(errors).flatMap(([field, messages]) =>
      Array.isArray(messages)
        ? messages.map((msg) => `${field}: ${msg}`)
        : [`${field}: ${messages}`]
    );
  }
  return [String(errors)];
}

/** True when payload indicates a confirmed success for legacy helpers. */
function payloadLooksSuccessful(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (hasMeaningfulErrors(payload.errors)) return false;
  if (payload.id || payload.data?.id) return true;
  if (payload.success) return true;
  if (Array.isArray(payload.messages) && payload.messages.length > 0) return true;
  // HTTP 204-style empty success often arrives as null/undefined earlier;
  // allow explicit no-content markers.
  if (payload === "" || payload.no_content === true) return true;
  return false;
}

export const dispatchWithToast = (dispatch, onRefresh, onClose) => {
  // When a dialog owns the active operation, skip toast.promise entirely.
  if (shouldSuppressToastForOperation()) {
    return Promise.resolve(dispatch).then((data) => {
      if (data?.meta?.requestStatus === "rejected") {
        throw errorFromRejectedThunk(data, { emitToast: false });
      }
      if (isFalsyThunkPayload(data?.payload)) {
        throw errorFromRejectedThunk(data, { emitToast: false });
      }
      if (hasMeaningfulErrors(data?.payload?.errors)) {
        throw new Error("Server error");
      }
      if (
        payloadLooksSuccessful(data?.payload) ||
        (data?.payload != null && !isFalsyThunkPayload(data?.payload))
      ) {
        if (onRefresh) onRefresh(data?.payload);
        if (onClose) onClose(true);
      }
      return data;
    });
  }

  return toast.promise(dispatch, {
    loading: "Saving...",
    success: (data) => {
      if (data?.meta?.requestStatus === "rejected") {
        // Single presentation: promise catch owns the error toast.
        throw errorFromRejectedThunk(data, { emitToast: false });
      }

      if (isFalsyThunkPayload(data?.payload)) {
        throw errorFromRejectedThunk(data, { emitToast: false });
      }

      if (hasMeaningfulErrors(data?.payload?.errors)) {
        const msgs = flattenErrorMessages(data.payload.errors);
        throw new Error(msgs[0] || "Server error");
      }

      if (data?.payload?.messages) {
        data.payload.messages.forEach((message) => {
          toast.success(message, { duration: SIMPLE_SUCCESS_TOAST_MS });
        });
      }

      if (data?.payload?.id || data?.payload?.data?.id) {
        if (onRefresh) onRefresh(data?.payload);
        if (onClose) onClose(true);
        return "Saved successfully";
      }

      if (
        data?.payload?.success ||
        (data?.payload?.messages &&
          !data?.payload?.id &&
          !data?.payload?.data?.id)
      ) {
        if (onRefresh) onRefresh(data?.payload);
        if (onClose) onClose(true);
        return "Saved successfully";
      }

      // Empty errors collection with otherwise empty body: treat as success when
      // the thunk fulfilled (legacy 204 / no-content contracts).
      // Explicitly exclude `false` (agreements soft-failure pattern).
      if (
        data?.meta?.requestStatus === "fulfilled" &&
        data?.payload != null &&
        !isFalsyThunkPayload(data?.payload) &&
        !hasMeaningfulErrors(data?.payload?.errors)
      ) {
        if (onRefresh) onRefresh(data?.payload);
        if (onClose) onClose(true);
        return "Saved successfully";
      }

      if (!data?.payload?.id && !data?.payload?.data?.id) {
        throw new Error("Server error");
      }

      throw new Error("Server error");
    },
    error: (error) => {
      console.error(error);
      return error?.message || "Something went wrong. Could not save data!";
    },
  });
};

export const dispatchWithToastV2 = (
  dispatchAction,
  {
    onRefresh,
    onClose,
    setData,
    setErrors,
    onError,
    savingText = "Saving...",
    /** Skip the success toast (e.g. when the caller shows its own confirmation UI). */
    suppressSuccessToast = false,
    /** When true (or dialog owns presentation), never emit toast side-effects. */
    suppressToasts = false,
    clientOperationId,
  } = {}
) => {
  const dialogOwns =
    suppressToasts || shouldSuppressToastForOperation(clientOperationId);

  const emitError = (msg) => {
    if (!dialogOwns) toast.error(msg, { persistent: true });
  };
  const emitSuccess = (msg) => {
    if (!dialogOwns) toast.success(msg, { duration: SIMPLE_SUCCESS_TOAST_MS });
  };

  // Variant without a success toast: keep loading + error handling, but no
  // "saved" toast. Used where the component renders its own success screen.
  if (suppressSuccessToast || dialogOwns) {
    const loadingId = dialogOwns ? null : toast.loading(savingText);
    return Promise.resolve(dispatchAction)
      .then((response) => {
        if (response?.meta?.requestStatus === "rejected") {
          const payload = response?.payload;
          if (hasMeaningfulErrors(payload?.errors)) {
            if (setErrors) setErrors(payload.errors);
            if (onError) onError(payload);
            if (!setErrors && !onError) {
              flattenErrorMessages(payload.errors).forEach(emitError);
            }
            return;
          }
          if (onError) onError(payload);
          else emitError(payload?.message || "Request failed");
          return;
        }

        const payload = response?.payload;
        if (isFalsyThunkPayload(payload)) {
          if (onError) onError(payload);
          else emitError("Request failed");
          return;
        }

        const errors = payload?.errors;
        const messages = payload?.messages;
        const hasId = payload?.id || payload?.data?.id;

        if (hasMeaningfulErrors(errors)) {
          if (setErrors) setErrors(errors);
          if (onError) onError(payload);
          if (!setErrors && !onError) {
            flattenErrorMessages(errors).forEach(emitError);
          }
          return;
        }

        if (messages && Array.isArray(messages) && !suppressSuccessToast && !dialogOwns) {
          messages.forEach((msg) => emitSuccess(msg));
        }

        if (hasId || payload?.success || payloadLooksSuccessful(payload)) {
          if (setData) setData(payload);
          if (onRefresh) onRefresh(payload);
          if (onClose) onClose(true);
          return;
        }

        if (onError) onError(payload);
      })
      .catch((error) => {
        const responseErrors = error?.response?.data?.errors;
        if (hasMeaningfulErrors(responseErrors)) {
          if (setErrors) setErrors(responseErrors);
          if (onError) onError(error);
          if (!setErrors && !onError) {
            flattenErrorMessages(responseErrors).forEach(emitError);
          }
        } else if (onError) {
          onError(error);
        } else {
          emitError(error?.message || "An unexpected error occurred.");
        }
      })
      .finally(() => {
        if (loadingId) toast.dismiss(loadingId);
      });
  }

  return toast.promise(dispatchAction, {
    loading: savingText,
    success: (response) => {
      if (response?.meta?.requestStatus === "rejected") {
        throw errorFromRejectedThunk(response, { emitToast: false });
      }

      const payload = response?.payload;
      if (isFalsyThunkPayload(payload)) {
        if (onError) onError(payload);
        throw errorFromRejectedThunk(response, { emitToast: false });
      }

      const errors = payload?.errors;
      const messages = payload?.messages;
      const hasId = payload?.id || payload?.data?.id;

      if (hasMeaningfulErrors(errors)) {
        const errorMessages = flattenErrorMessages(errors);
        if (setErrors) setErrors(errors);
        if (onError) onError(payload);
        // Single presentation via promise catch — do not toast.error here.
        throw new Error(errorMessages[0] || "Validation failed");
      }

      if (messages && Array.isArray(messages)) {
        messages.forEach((msg) =>
          toast.success(msg, { duration: SIMPLE_SUCCESS_TOAST_MS })
        );
      }

      if (hasId || payload?.success || payloadLooksSuccessful(payload)) {
        if (setData) setData(payload);
        if (onRefresh) onRefresh(payload);
        if (onClose) onClose(true);
        return "Saved successfully";
      }

      if (onError) onError(payload);
      throw new Error("Unexpected response from server");
    },

    error: (error) => {
      const responseErrors = error?.response?.data?.errors;
      if (hasMeaningfulErrors(responseErrors)) {
        const errorMessages = flattenErrorMessages(responseErrors);
        if (setErrors) setErrors(responseErrors);
        if (onError) onError(error);
        return errorMessages[0] || "Validation error occurred.";
      }

      if (onError) onError(error);
      return error?.message || "An unexpected error occurred.";
    },
  });
};

// New version that returns a proper promise for error handling
export const dispatchWithToastPromise = (dispatchAction, onRefresh, onClose) => {
  if (shouldSuppressToastForOperation()) {
    return Promise.resolve(dispatchAction).then((data) => {
      if (data?.meta?.requestStatus === "rejected") {
        return Promise.reject(errorFromRejectedThunk(data, { emitToast: false }));
      }
      if (isFalsyThunkPayload(data?.payload)) {
        return Promise.reject(errorFromRejectedThunk(data, { emitToast: false }));
      }
      if (hasMeaningfulErrors(data?.payload?.errors)) {
        return Promise.reject(new Error("Server error"));
      }
      if (onRefresh) onRefresh(data?.payload);
      if (onClose) onClose(true);
      return data?.payload;
    });
  }

  return toast
    .promise(dispatchAction, {
      loading: "Saving...",
      success: (data) => {
        if (data?.meta?.requestStatus === "rejected") {
          throw errorFromRejectedThunk(data, { emitToast: false });
        }

        if (isFalsyThunkPayload(data?.payload)) {
          throw errorFromRejectedThunk(data, { emitToast: false });
        }

        if (hasMeaningfulErrors(data?.payload?.errors)) {
          const msgs = flattenErrorMessages(data.payload.errors);
          throw new Error(msgs[0] || "Server error");
        }

        if (data?.payload?.messages) {
          data.payload.messages.forEach((message) => {
            toast.success(message, { duration: SIMPLE_SUCCESS_TOAST_MS });
          });
        }

        const ok =
          data?.payload?.id ||
          data?.payload?.data?.id ||
          data?.payload?.success ||
          (data?.payload?.messages &&
            !data?.payload?.id &&
            !data?.payload?.data?.id) ||
          (data?.meta?.requestStatus === "fulfilled" &&
            data?.payload != null &&
            !isFalsyThunkPayload(data?.payload) &&
            !hasMeaningfulErrors(data?.payload?.errors));

        if (!ok) {
          throw new Error("Server error");
        }

        if (onRefresh) onRefresh(data?.payload);
        if (onClose) onClose(true);
        return "Saved successfully";
      },
      error: (error) => {
        console.error(error);
        return error?.message || "Something went wrong. Could not save data!";
      },
    })
    .then((data) => data?.payload);
};

export const axiosWithToast = (
  action,
  onRefresh = () => {},
  onClose = () => {},
  setData = () => {},
  savingText = "Saving...",
  setErrors = () => {}
) => {
  const dialogOwns = shouldSuppressToastForOperation();

  const handleSuccessBody = (res) => {
    // FE-UT-01: resolved AxiosError / Error / HTTP≥400 must not hit success toast.
    if (isResolvedFailureEnvelope(res)) {
      const errBody = res?.response?.data ?? res?.data ?? res;
      if (hasMeaningfulErrors(errBody?.errors)) {
        if (setErrors) setErrors(errBody.errors);
        const msgs = flattenErrorMessages(errBody.errors);
        throw Object.assign(
          new Error(msgs[0] || res?.message || "Request failed"),
          { payload: errBody, isAxiosError: Boolean(res?.isAxiosError) }
        );
      }
      throw Object.assign(
        new Error(res?.message || "Request failed"),
        {
          isAxiosError: Boolean(res?.isAxiosError),
          response: res?.response,
          payload: errBody,
        }
      );
    }

    // Axios 204: res.data may be empty string / undefined — that is success.
    if (res?.status === 204 || res?.status === 202) {
      const responseData = res?.data ?? null;
      if (setData) setData(responseData);
      if (onRefresh) onRefresh(responseData);
      if (onClose) onClose(true);
      return res?.status === 202 ? "Request accepted" : "Saved successfully";
    }

    const response = res?.data !== undefined ? res.data : res;
    if (response === "" || response == null) {
      if (setData) setData(response);
      if (onRefresh) onRefresh(response);
      if (onClose) onClose(true);
      return "Saved successfully";
    }

    // FE-UT-02: single presentation — throw; promise catch owns the one error toast.
    if (hasMeaningfulErrors(response?.errors)) {
      if (setErrors) setErrors(response.errors);
      const msgs = flattenErrorMessages(response.errors);
      throw new Error(msgs[0] || "Server error: Validation errors occurred.");
    }

    if (hasMeaningfulErrors(response?.data?.errors)) {
      if (setErrors) setErrors(response.data.errors);
      const msgs = flattenErrorMessages(response.data.errors);
      throw new Error(msgs[0] || "Server error: Validation errors occurred.");
    }

    if (response?.data?.messages && !dialogOwns) {
      response.data.messages.forEach((message) => {
        toast.success(message, { duration: SIMPLE_SUCCESS_TOAST_MS });
      });
    }

    const responseData = response?.data || response;
    const hasId = responseData?.id || responseData?.data?.id;
    const hasData =
      responseData &&
      (typeof responseData === "object"
        ? Object.keys(responseData).length > 0
        : true);

    if (hasId || hasData || responseData === null) {
      if (setData) setData(responseData);
      if (onRefresh) onRefresh(responseData);
      if (onClose) onClose(true);
      return "Saved successfully";
    }
    console.warn("Unexpected response format:", responseData);
    throw new Error("Server error: Unexpected response format.");
  };

  if (dialogOwns) {
    return Promise.resolve(action).then(handleSuccessBody).catch((res) => {
      const error = res?.data || res;
      if (hasMeaningfulErrors(error?.response?.data?.errors)) {
        if (setErrors) setErrors(error.response.data.errors);
      }
      throw error;
    });
  }

  return toast.promise(action, {
    loading: savingText,
    success: handleSuccessBody,
    error: (res) => {
      const error = res?.data || res;

      console.error(
        "Error details:",
        error?.response || error?.message || "Unknown error"
      );

      if (hasMeaningfulErrors(error?.response?.data?.errors)) {
        if (setErrors) setErrors(error.response.data.errors);
        return (
          flattenErrorMessages(error.response.data.errors)[0] ||
          "An error occurred."
        );
      }

      return error?.message || "An unexpected error occurred. Please try again.";
    },
  });
};

